// src/store/thunks/appointmentsThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

export const createAppointment = createAsyncThunk(
  "appointments/createAppointment",
  async (appointmentData, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointments`,
        appointmentData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update an existing appointment
export const updateAppointment = createAsyncThunk(
  "appointments/updateAppointment",
  async ({ id, updatedData }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/${id}`,
        updatedData,
        config
      );
      // Extract the appointment from the response - API returns { appointment, conflictsDetected, conflicts }
      return response.data.appointment;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update an appointment clinician with future appointment options
export const updateAppointmentClinician = createAsyncThunk(
  "appointments/updateAppointmentClinician",
  async ({ id, clinicianType, clinicianId, updateAllFuture }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/${id}/clinician`,
        { clinicianType, clinicianId, updateAllFuture },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk to update appointment history
export const updateAppointmentHistory = createAsyncThunk(
  "appointments/updateHistory",
  async ({ id, historyItem }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/update-history/${id}`,
        { historyItem },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsList = createAsyncThunk(
  "appointments/fetchAppointmentsList",
  async (_, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(`${baseURL}/api/appointments`, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByClientId = createAsyncThunk(
  "appointments/fetchAppointmentsByClientId",
  async (clientId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/client/id/${clientId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsWithSoapByClientId = createAsyncThunk(
  "appointments/fetchAppointmentsWithSoapByClientId",
  async (clientId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/soaps/client/id/${clientId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByClinicianId = createAsyncThunk(
  "appointments/fetchAppointmentsByClinician",
  async (clinicianId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/clinician/id/${clinicianId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentById = createAsyncThunk(
  "appointments/fetchAppointmentById",
  async (appointmentId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/id/${appointmentId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// NEW - AUG 2
// Thunk for fetching appointments by date range
export const fetchAppointmentsByDateRange = createAsyncThunk(
  "appointments/fetchByDateRange",
  async ({ startDate, endDate }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const url = `${baseURL}/api/appointments/date-range?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// This is exclusively for HRPayrollOverviewPage
export const fetchAllAppointmentsByDateRange = createAsyncThunk(
  "appointments/fetchAllAppointmentsByDateRange",
  async ({ startDate, endDate, cohortId = null, clinicId = null }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      // Dynamically construct URL based on provided parameters
      let url = `${baseURL}/api/appointments/payroll/hr/allClinicians/byDateRange`;
      const params = [];

      if (startDate) params.push(`startDate=${startDate.toISOString()}`);
      if (endDate) params.push(`endDate=${endDate.toISOString()}`);
      
      // Add cohort filter if specified
      if (cohortId && cohortId !== "All") {
        params.push(`cohortId=${cohortId}`);
      }
      
      // Add clinic filter if specified
      if (clinicId && clinicId !== "All") {
        params.push(`clinicId=${clinicId}`);
      }

      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// NEW - Aug 21st
// Thunk for fetching appointments only for specific date
export const fetchAppointmentsByDate = createAsyncThunk(
  "appointments/fetchByDate",
  async ({ date, clinicId }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      // Prepare the request body
      const requestBody = {
        requestedDate: new Date(date).toISOString(),
      };

      // Add clinicId to the request body if it's provided
      if (clinicId) {
        requestBody.clinicId = clinicId;
      }

      // Pass the date and optional clinicId as data in the request body
      const response = await axios.post(
        `${baseURL}/api/appointments/date`,
        requestBody,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk for fetching appointments by date range
export const fetchAppointmentsByDateRangeOnly = createAsyncThunk(
  "appointments/fetchByDateRangeOnly",
  async ({ startDate, endDate, clinicId }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      let url = `${baseURL}/api/appointments/date-range?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      
      // Add clinic filtering if clinicId is provided
      if (clinicId) {
        url += `&clinicId=${clinicId}`;
      }
      
      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByClinicianAndDateRange = createAsyncThunk(
  "appointments/fetchByClinicianAndDateRange",
  async (
    { clinicianId, startDate, endDate },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const url = `${baseURL}/api/appointments/clinician/${clinicianId}/dateRange?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;

      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByClinicianDateRangeAndStatus = createAsyncThunk(
  "appointments/fetchByClinicianDateRangeAndStatus",
  async (
    { clinicianId = null, startDate, endDate, statuses, excludeStatus = false }, // Make clinicianId optional
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/payroll/${
          clinicianId ? clinicianId : "all"
        }/byDateRangeAndStatus?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&statuses=${statuses.join(
          ","
        )}&excludeStatus=${excludeStatus}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchCarryoverAppointments = createAsyncThunk(
  "appointments/fetchCarryoverAppointments",
  async (
    { clinicianId = null, startDate }, // clinicianId is now optional
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/payroll/${
          clinicianId ? clinicianId : "all"
        }/carryover?startDate=${startDate.toISOString()}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchSoapNotes = createAsyncThunk(
  "appointments/fetchSoapNotes",
  async (_, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/soapNotes`,
        config
      );
      return response.data; // Return the raw data without any extra formatting
    } catch (error) {
      console.log("Error fetching SOAP notes:", error);
      return rejectWithValue(
        error.response?.data || "Failed to fetch SOAP notes"
      );
    }
  }
);

export const fetchSoapNotesByClientId = createAsyncThunk(
  "appointments/fetchSoapNotesByClientId",
  async (clientId, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/soapNotes/${clientId}`,
        config
      );
      return response.data; // Return the raw data without any extra formatting
    } catch (error) {
      console.log("Error fetching SOAP notes:", error);
      return rejectWithValue(
        error.response?.data || "Failed to fetch SOAP notes"
      );
    }
  }
);

export const saveSoapNote = createAsyncThunk(
  "appointments/saveSoapNote",
  async (
    [appointment, user, soapNote],
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointments/saveSoap`,
        {
          appointment,
          user,
          soapNote,
        },
        config
      );
      console.log("Payload:", { appointment, soapNote }); // Debugging line
      console.log("Saved SOAP Note Response:", response.data); // Log saved SOAP note response

      return response.data;
    } catch (error) {
      console.log("Error Saving SOAP Note:", error); // Log error
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const updateSoapNote = createAsyncThunk(
  "appointments/updateSoapNote",
  async (
    [appointment, user, soapNote],
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.put(
        `${baseURL}/api/appointments/updateSoap`,
        {
          appointment: { _id: appointment._id },
          user: { _id: user._id },
          soapNote,
        },
        config
      );
      console.log("Updated SOAP Note Response:", response.data);
      return response.data;
    } catch (error) {
      console.log("Error Updating SOAP Note:", error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const completeSoapNote = createAsyncThunk(
  "appointments/completeSoapNote",
  async ([appointment, user], { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointments/completeSoap`,
        { appointment, user },
        config
      );
      console.log("SOAP Note Completed Response:", response.data); // Debugging line
      return response.data;
    } catch (error) {
      console.log("Error Completing SOAP Note:", error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const approveSoapNote = createAsyncThunk(
  "appointments/approveSoapNote",
  async (
    { appointment, user, finalNote, supervisorComment },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointments/approveSoap`,
        {
          appointment,
          user,
          finalNote,
          supervisorComment,
        },
        config
      );

      console.log("Approve SOAP Note Response:", response.data);
      return response.data;
    } catch (error) {
      console.log("Error Signing Off SOAP Note:", error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk to fetch the preceding SOAP note
export const fetchPrecedingSoapNote = createAsyncThunk(
  "appointments/fetchPrecedingSoapNote",
  async (appointmentId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/appointments/preceding-soap-note/${appointmentId}`,
        config
      );

      if (response.status !== 200) {
        throw new Error("Failed to fetch preceding SOAP note");
      }

      return response.data.soapNote;
    } catch (error) {
      console.error("Error fetching preceding SOAP note:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch SOAP note"
      );
    }
  }
);

export const saveAppointment = createAsyncThunk(
  "appointments/saveAppointment",
  async (appointmentData, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/${appointmentData._id}`,
        appointmentData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const updateAppointmentStatus = createAsyncThunk(
  "appointments/updateAppointmentStatus",
  async (
    { appointmentId, status, comments, userId },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/status/${appointmentId}`,
        { status, comments, userId },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk for manually updating appointment statuses (for owners) with history tracking
export const updateAppointmentStatusManual = createAsyncThunk(
  "appointments/updateAppointmentStatusManual",
  async (
    { appointmentId, status, userId, comments },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/manual-status/${appointmentId}`,
        { status, userId, comments },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const revertTreated = createAsyncThunk(
  "appointments/revertTreated",
  async (
    { appointmentId, status, comments, userId },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/appointments/revertTreated/${appointmentId}`,
        { status, comments, userId },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const notifyClientByEmail = createAsyncThunk(
  "appointments/notifyClientByEmail",
  async ({ appointment, user }, { rejectWithValue }) => {
    const { baseURL, config } = generateConfig({ token: user.token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointments/notify`,
        { appointment, user },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const checkForConflicts = createAsyncThunk(
  "appointments/checkForConflicts",
  async ({ dates, clinicianId }, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointments/checkConflicts`,
        { dates, clinicianId },
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const rescheduleAppointment = createAsyncThunk(
  "appointments/rescheduleAppointment",
  async (appointmentData, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/appointment/reschedule`,
        appointmentData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByResourceId = createAsyncThunk(
  "appointments/fetchByResourceId",
  async (resourceId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/appointments/resource/${resourceId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByClinicianAndDay = createAsyncThunk(
  "appointments/fetchByClinicianAndDay",
  async (
    { clinicianId, date },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const url = `${baseURL}/api/appointments/clinician/${clinicianId}/date/${date}`;
      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// This is exclusively for HRIndividualPayrollPage
export const fetchClinicianAppointmentsByDateRange = createAsyncThunk(
  "appointments/fetchClinicianAppointmentsByDateRange",
  async ({ clinicianId, endDate, clinicId = null }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      let url = `${baseURL}/api/appointments/payroll/hr/clinician/${clinicianId}/byDateRange`;
      const params = [];

      if (endDate) params.push(`endDate=${endDate.toISOString()}`);
      
      // Add clinic filter if specified
      if (clinicId && clinicId !== "All") {
        params.push(`clinicId=${clinicId}`);
      }

      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Conflict management thunks
export const fetchConflictedAppointments = createAsyncThunk(
  "appointments/fetchConflictedAppointments",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/appointments/conflicts`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const resolveAppointmentConflict = createAsyncThunk(
  "appointments/resolveAppointmentConflict",
  async (
    { appointmentId, resolution, resolvedBy },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/appointments/${appointmentId}/resolve-conflict`,
        { resolution, resolvedBy },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const checkAppointmentConflicts = createAsyncThunk(
  "appointments/checkAppointmentConflicts",
  async (
    { appointmentId },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/appointments/${appointmentId}/check-conflicts`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const unapproveSoapNote = createAsyncThunk(
  "appointments/unapproveSoapNote",
  async (
    { appointment, user, reason },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });
    try {
      const response = await axios.post(
        `${baseURL}/api/appointments/unapproveSoap`,
        { appointment, user, reason },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAppointmentsByClinicianIdAllClinics = createAsyncThunk(
  "appointments/fetchAppointmentsByClinicianAllClinics",
  async (clinicianId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });
    try {
      console.log(`[fetchAppointmentsByClinicianIdAllClinics] Fetching all appointments for clinician ${clinicianId}`);
      const response = await axios.get(
        `${baseURL}/api/appointments/clinician/id/${clinicianId}/all-clinics`,
        config
      );
      console.log(`[fetchAppointmentsByClinicianIdAllClinics] Found ${response.data.length} appointments`);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
